import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';
import { AIConversationService } from '../../core/services/ai-conversation.service';
import { askFailureKeys } from '../ask/ask-failure';

/**
 * RESPONSIBILITY: announces the assistant's answer to a screen reader.
 *
 * The transcript is a list of plain elements. Nothing about appending one
 * tells assistive technology that anything happened, so a screen-reader user
 * could send a message and be told only that the send button had been
 * pressed — the reply itself arrived in silence, on both the docked panel and
 * the /ai page. The shell bar's `paper-ai-trigger` announces the STATE
 * ("Thinking…"), which is why this was easy to miss: something spoke, so it
 * sounded handled. It never read the answer.
 *
 * WHAT IT ANNOUNCES: the completed assistant turn, once, on the streaming
 * true → false edge. Not the tokens as they arrive — a polite region that
 * changes thirty times a second is re-read from the top each time and
 * produces noise rather than an answer. Not the user's own message either;
 * they typed it.
 *
 * MOUNTED: ONCE, in AppShellComponent, deliberately not in the two chat
 * surfaces. `AIConversationService` is root-provided and the panel can be
 * open while /ai is routed, so a per-surface region would be two regions
 * reading one answer.
 */
@Component({
  selector: 'im-ai-live-region',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template:
    '<p class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ announcement() }}</p>',
})
export class AiLiveRegionComponent {
  private readonly conversation = inject(AIConversationService);
  private readonly transloco = inject(TranslocoService);

  /** The text currently exposed to assistive technology. */
  protected readonly announcement = signal('');

  /**
   * Previous streaming value. A plain field, not a signal: the effect below
   * writes it, and a signal here would make the effect dirty itself on every
   * run — the `43fa13ba` freeze shape.
   */
  private wasStreaming = false;

  constructor() {
    effect(() => {
      const streaming = this.conversation.isStreaming();
      const messages = this.conversation.messages();

      untracked(() => {
        const finished = this.wasStreaming && !streaming;
        this.wasStreaming = streaming;
        if (!finished) return;

        const last = messages.at(-1);
        if (last?.role !== 'assistant') return;

        // A failed turn has no prose: say what went wrong, as the thread's
        // failure card does, in the viewer's language.
        if (last.failure) {
          const keys = askFailureKeys(last.failure);
          this.announcement.set(`${this.transloco.translate(keys.title)}. ${this.transloco.translate(keys.body)}`);
          return;
        }

        const text = last.content.trim();
        // A widget-only turn (an availability list, a booking receipt) can
        // carry no prose. Saying nothing is better than announcing an empty
        // string, which some readers voice as "blank".
        if (!text) return;

        this.announcement.set(text);
      });
    });
  }
}
